(function () {
  const currentScript = document.currentScript;

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
  
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
    }
  
    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
  
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        if (this.cartDependencyFailed) return;
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (this.cartDependencyFailed) return;
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  
    open(triggeredBy) {
      if (this.cartDependencyFailed) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
  
      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );
  
      document.body.classList.add('overflow-hidden');
    }
  
    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }
  
    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');
  
      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }
  
      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });
  
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  
    renderContents(parsedState) {
      if (this.cartDependencyFailed) {
        this.open();
        return;
      }
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
  
        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
  
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }
  
    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }
  
    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  function registerDrawerItems() {
    if (customElements.get('cart-drawer-items')) return true;
    const BaseCartItems = customElements.get('cart-items');
    if (!BaseCartItems) return false;

    class CartDrawerItems extends BaseCartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }
    
    customElements.define('cart-drawer-items', CartDrawerItems);
    return true;
  }

  if (registerDrawerItems() || window.__sensorTagsCartDependencyRecovery) return;
  const recovery = window.__sensorTagsCartDependencyRecovery = { retryStarted: false, reported: false };
  let dependencyScript;
  let originalSettled = false;
  let timer;

  function cleanup() {
    window.clearTimeout(timer);
    document.removeEventListener('DOMContentLoaded', onDocumentReady);
    window.removeEventListener('load', onDocumentReady);
    dependencyScript?.removeEventListener('load', onOriginalSettled);
    dependencyScript?.removeEventListener('error', onOriginalSettled);
  }

  function finishIfReady() {
    if (!registerDrawerItems()) return false;
    cleanup();
    return true;
  }

  function reportFailure() {
    if (finishIfReady() || recovery.reported) return;
    recovery.reported = true;
    cleanup();
    const forms = document.querySelectorAll('product-form');
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      drawer.cartDependencyFailed = true;
      forms.forEach((form) => {
        if (form.cart === drawer) {
          form.submitButton?.removeAttribute('aria-haspopup');
        }
      });
      drawer.hidden = true;
      drawer.remove();
      try {
        if (typeof removeTrapFocus === 'function') removeTrapFocus(drawer.activeElement);
      } catch {}
    });
    document.body.classList.remove('overflow-hidden');
    const cartLink = document.getElementById('cart-icon-bubble');
    cartLink?.removeAttribute('role');
    cartLink?.removeAttribute('aria-haspopup');
    cartLink?.removeAttribute('aria-expanded');
    cartLink?.removeAttribute('aria-controls');
    try {
      window.SensorTagsMonitoring?.captureTechnicalFailure('cart_dependency_failed');
    } catch {}
  }

  function retryDependency() {
    if (finishIfReady() || recovery.retryStarted) return;
    if (!dependencyScript) {
      reportFailure();
      return;
    }
    recovery.retryStarted = true;
    cleanup();
    const retry = document.createElement('script');
    retry.src = dependencyScript.src;
    retry.async = false;
    for (const attribute of ['integrity', 'crossorigin', 'referrerpolicy', 'fetchpriority']) {
      if (dependencyScript.hasAttribute(attribute)) {
        retry.setAttribute(attribute, dependencyScript.getAttribute(attribute));
      }
    }
    if (dependencyScript.nonce) retry.nonce = dependencyScript.nonce;
    retry.setAttribute('data-sensortags-cart-retry', '');
    retry.onload = () => {
      if (!finishIfReady()) reportFailure();
    };
    retry.onerror = reportFailure;
    timer = window.setTimeout(reportFailure, 8000);
    try {
      document.head.appendChild(retry);
    } catch {
      reportFailure();
    }
  }

  function onOriginalSettled() {
    originalSettled = true;
    if (!finishIfReady()) retryDependency();
  }

  function onDocumentReady() {
    if (finishIfReady()) return;
    if (!dependencyScript) {
      reportFailure();
      return;
    }
    if (originalSettled || (dependencyScript.defer && !dependencyScript.async)) retryDependency();
  }

  try {
    const dependencyUrl = new URL('./cart.js', currentScript.src);
    dependencyScript = Array.from(document.scripts).find((script) => {
      if (script === currentScript || !script.src) return false;
      const url = new URL(script.src, document.baseURI);
      return url.origin === dependencyUrl.origin && url.pathname === dependencyUrl.pathname;
    });
  } catch {}

  customElements.whenDefined('cart-items').then(finishIfReady);
  dependencyScript?.addEventListener('load', onOriginalSettled, { once: true });
  dependencyScript?.addEventListener('error', onOriginalSettled, { once: true });
  document.addEventListener('DOMContentLoaded', onDocumentReady, { once: true });
  window.addEventListener('load', onDocumentReady, { once: true });
  timer = window.setTimeout(reportFailure, 8000);
  if (document.readyState === 'complete') onDocumentReady();
})();

